import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import jeinarStoryTranslations from '../translations/jeinarStoryTranslations';

const TheStoryOfJeinarValouse = () => {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang') || 'fr';
  const t = (obj) => obj?.[lang] || obj?.fr || '';
  const tr = jeinarStoryTranslations;
  const backLink = '/valouse#faq-jeinar';

  useEffect(() => {
    document.title = "Le Récit de Jeinar";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "Le récit de Jeinar - Une traversée vers soi-même. Fondatrice de COCON.");
  }, []);

  // Narrative Section — more breathing
  const NarrativeSection = ({ title, children, className = "", dark = false }) => (
    <section className={`py-20 sm:py-28 px-5 sm:px-6 ${className}`}>
      <div className="max-w-2xl mx-auto">
        {title && (
          <div className="text-center mb-16">
            <div className={`w-16 h-px mx-auto mb-10 ${dark ? 'bg-[#C5A059]' : 'bg-[#C5A059]/50'}`} />
            <h2 
              className={`font-serif text-xl sm:text-2xl ${dark ? 'text-[#E8D2B5]' : 'text-[#3F2A24]'}`}
              style={{ letterSpacing: '0.05em' }}
            >
              {title}
            </h2>
          </div>
        )}
        <div className="space-y-10 text-center">
          {children}
        </div>
      </div>
    </section>
  );

  // Poetic text — more line height
  const P = ({ children, className = "", accent = false }) => (
    <p 
      className={`text-[#3F2A24] whitespace-pre-line ${accent ? 'text-lg sm:text-xl font-serif italic' : 'text-base sm:text-lg'} ${className}`}
      style={{ lineHeight: '2.2', textWrap: 'balance' }}
    >
      {children}
    </p>
  );

  // Gold highlight
  const Gold = ({ children, large = false }) => (
    <p 
      className={`text-[#C5A059] font-serif italic ${large ? 'text-2xl sm:text-3xl py-8' : 'text-xl sm:text-2xl py-6'}`}
      style={{ lineHeight: '1.8', textWrap: 'balance' }}
    >
      {children}
    </p>
  );

  // Isolated phrase — centered, extra breathing
  const Breath = ({ children }) => (
    <div className="py-4">
      <p 
        className="text-[#3F2A24]/60 font-serif text-base sm:text-lg italic"
        style={{ lineHeight: '2.2', textWrap: 'balance' }}
      >
        {children}
      </p>
    </div>
  );

  // Separator
  const Sep = () => (
    <div className="py-2">
      <div className="w-8 h-px bg-[#C5A059]/30 mx-auto" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F3E5CF]">
      
      {/* Back Button */}
      <div className="fixed top-4 left-4 z-40">
        <Link
          to={backLink}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3F2A24]/10 hover:bg-[#3F2A24]/20 backdrop-blur-sm transition-colors text-[#3F2A24] text-sm"
          data-testid="back-from-story"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour</span>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-5 sm:px-6">
        <div 
          className="absolute inset-0 bg-cover"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_c4b2413e-fbb2-49c4-b5be-8ac09232191c/artifacts/9tuzv9q7_B672DDA7-8A5B-4BE7-B8EE-F178708CA008_1_105_c.jpeg)',
            backgroundPosition: 'center 21%'
          }}
          role="img"
          aria-label="Jeinar en méditation, fondatrice de COCON"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3F2A24]/60 via-[#3F2A24]/40 to-[#F3E5CF]" />
        
        <div className="relative z-10 text-center pt-20 pb-32">
          <p className="text-xs tracking-[0.4em] uppercase text-[#F3E5CF]/80 mb-6">
            {t(tr.hero.subtitle)}
          </p>
          <h1 
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F3E5CF] mb-4"
            style={{ letterSpacing: '0.08em' }}
          >
            JEINAR
          </h1>
          <div className="w-24 h-px bg-[#C5A059] mx-auto mt-8" />
        </div>
      </section>

      {/* Opening Section */}
      <NarrativeSection className="bg-[#F3E5CF] -mt-16 pt-28">
        <P accent>{t(tr.opening.p1)}</P>
        
        <Sep />
        
        <P>{t(tr.opening.p2)}</P>
        
        <Breath>{t(tr.opening.p3)}</Breath>
        
        <Gold>
          {t(tr.opening.p4)}
        </Gold>
        
        <P className="text-[#3F2A24]/70">{t(tr.opening.p5)}</P>
        
        <Sep />
        
        <div className="pt-8">
          <P accent>{t(tr.opening.greeting)}</P>
        </div>
      </NarrativeSection>

      {/* Origins Section */}
      <NarrativeSection title="Origines" className="bg-[#E8D2B5]">
        <P>{t(tr.origins.p1)}</P>
        
        <P>{t(tr.origins.p2)}</P>
        <Breath>{t(tr.origins.p3)}</Breath>
        
        <Sep />
        
        <P>{t(tr.origins.p4)}</P>
        <P>{t(tr.origins.p5)}</P>
        
        <Breath>
          {t(tr.origins.p6)}
        </Breath>
        
        <P>{t(tr.origins.p7)}</P>
        
        <Gold>{t(tr.origins.p8)}</Gold>
      </NarrativeSection>

      {/* The Moment Section */}
      <NarrativeSection title="Le moment où tout a basculé" className="bg-[#3F2A24]" dark>
        <P className="!text-[#F3E5CF]">{t(tr.theMoment.p1)}</P>
        
        <Sep />
        
        <P className="!text-[#F3E5CF]/80">{t(tr.theMoment.p2)}</P>
        
        <P className="!text-[#F3E5CF]/70">{t(tr.theMoment.p3)}</P>
        
        <p className="text-[#C5A059] font-serif text-3xl sm:text-4xl italic py-10">
          {t(tr.theMoment.p4)}
        </p>
        
        <P className="!text-[#F3E5CF]">{t(tr.theMoment.p5)}</P>
        
        <Sep />
        
        <P className="!text-[#F3E5CF]/80">{t(tr.theMoment.p6)}</P>
        
        <P className="!text-[#F3E5CF]" accent>{t(tr.theMoment.p7)}</P>
      </NarrativeSection>

      {/* Silence Section */}
      <NarrativeSection title="La descente dans le silence" className="bg-[#F3E5CF]">
        <P>{t(tr.silence.p1)}</P>
        
        <P className="text-[#3F2A24]/80">{t(tr.silence.p2)}</P>
        
        <Sep />
        
        <P>{t(tr.silence.p3)}</P>
        
        <Gold>{t(tr.silence.p4)}</Gold>
        
        <P>{t(tr.silence.p5)}</P>
        <P className="text-[#3F2A24]/70">{t(tr.silence.p6)}</P>
        
        <Sep />
        
        <Gold>{t(tr.silence.p7)}</Gold>
        
        <P className="text-[#3F2A24]/70">{t(tr.silence.p8)}</P>
        
        <Sep />
        
        <P accent>{t(tr.silence.p9)}</P>
      </NarrativeSection>

      {/* Becoming Section */}
      <NarrativeSection title="Devenir Jeinar" className="bg-[#E8D2B5]">
        <P>{t(tr.becoming.p1)}</P>
        
        <P className="text-[#3F2A24]/80">{t(tr.becoming.p2)}</P>
        
        <P>{t(tr.becoming.p3)}</P>
        
        <Sep />
        
        <Breath>{t(tr.becoming.p4)}</Breath>
        
        <Gold>{t(tr.becoming.p5)}</Gold>
        
        <P>{t(tr.becoming.p6)}</P>
        
        <P className="text-[#3F2A24]/80">{t(tr.becoming.p7)}</P>
        
        <Sep />
        
        <P accent>{t(tr.becoming.p8)}</P>
        
        <P className="text-[#3F2A24]/70">{t(tr.becoming.p9)}</P>
        
        <P>{t(tr.becoming.p10)}</P>
      </NarrativeSection>

      {/* Living Ritual Section */}
      {/* ═══ CONSERVATION TEMPORAIRE — ancien bg: bg-[#516258] ═══ */}
      <NarrativeSection title="Une vie comme rituel vivant" className="bg-[#8B7D5E]" dark>
        <P className="!text-[#F3E5CF]">{t(tr.livingRitual.p1)}</P>
        
        <P className="!text-[#F3E5CF]/80">{t(tr.livingRitual.p2)}</P>
        
        <P className="!text-[#F3E5CF]/70">{t(tr.livingRitual.p3)}</P>
        
        <P className="!text-[#F3E5CF]">{t(tr.livingRitual.p4)}</P>
        
        <Sep />
        
        <P className="!text-[#F3E5CF]/80">{t(tr.livingRitual.p5)}</P>
        
        <p className="text-[#C5A059] font-serif text-lg sm:text-xl italic py-4">
          {t(tr.livingRitual.p6)}
        </p>
        
        <P className="!text-[#F3E5CF]">{t(tr.livingRitual.p7)}</P>
        
        <p className="text-[#C5A059] font-serif text-2xl sm:text-3xl italic pt-6 pb-2">
          {t(tr.livingRitual.p8)}
        </p>
      </NarrativeSection>

      {/* What I Offer Section — légèrement mis en valeur */}
      <section className="py-24 sm:py-32 px-5 sm:px-6 bg-[#F3E5CF]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-16 h-px mx-auto mb-10 bg-[#C5A059]/50" />
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-4">
              {lang === 'en' ? 'Transmission' : 'Transmission'}
            </p>
            <h2 
              className="font-serif text-xl sm:text-2xl text-[#3F2A24]"
              style={{ letterSpacing: '0.05em' }}
            >
              {t(tr.whatIOffer.title)}
            </h2>
          </div>
          
          <div className="space-y-10 text-center">
            <P>{t(tr.whatIOffer.p1)}</P>
            
            <Gold>{t(tr.whatIOffer.p2)}</Gold>
            
            <P>{t(tr.whatIOffer.p3)}</P>
            
            <Sep />
            
            <P className="text-[#3F2A24]/80">{t(tr.whatIOffer.p4)}</P>
            
            <Breath>{t(tr.whatIOffer.p5)}</Breath>
            
            <P>{t(tr.whatIOffer.p6)}</P>
            
            <Sep />
            
            <P>{t(tr.whatIOffer.p7)}</P>
            
            <P className="text-[#3F2A24]/70">{t(tr.whatIOffer.p8)}</P>
            
            <Sep />
            
            <P>{t(tr.whatIOffer.p9)}</P>
            <P>{t(tr.whatIOffer.p10)}</P>
            <P accent>{t(tr.whatIOffer.p11)}</P>
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <NarrativeSection title="Si ma vie devait se résumer" className="bg-[#3F2A24]" dark>
        <P className="!text-[#F3E5CF]/80">{t(tr.closing.p1)}</P>
        
        <p className="text-[#C5A059] font-serif text-2xl sm:text-3xl italic py-10">
          {t(tr.closing.p2)}
        </p>
        
        <P className="!text-[#F3E5CF] pt-4" accent>{t(tr.closing.p3)}</P>
      </NarrativeSection>

      {/* Final CTA */}
      <section className="py-20 px-5 sm:px-6 bg-[#E8D2B5]">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <div className="w-16 h-px bg-[#C5A059]/50 mx-auto" />
          <div>
            <Link
              to="/valouse#faq-jeinar"
              className="inline-flex items-center gap-2 text-[#3F2A24]/50 text-xs tracking-[0.15em] uppercase hover:text-[#3F2A24] transition-colors min-h-[44px]"
              data-testid="jeinar-back-to-faq-valouse"
            >
              ← Retour au FAQ
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TheStoryOfJeinarValouse;
